/**
 * RecordLabel — a label in the middle of the record; on two of them, the track's title and
 * performer written into it.
 *
 * Task 24 drew «Ямайская» here alone; since 2026-09-30 it is any of the twelve the owner chose
 * (`lib/labels.ts`). Each is a ready picture per record (`components/labelArt.ts`): the
 * long-player's is whole, the single's a ring round the adapter. On the five paper ones the two
 * lines are written here, sized and cut by `lib/label.ts`, at the same places on all five — the
 * pictures leave those places free; their colour is the stylesheet's (`styles/deck.css`), black
 * but on the dark green of «Остров». There is no pattern to generate.
 *
 * It lies inside the turning disc (`components/Vinyl.tsx`), so it turns with the record and with
 * a finger on it; it renders when the track or the record changes, and never under a finger.
 *
 * The lines are text a screen reader would read in the middle of a picture, twice over: the
 * title is already the screen's heading. So the label is a picture with no name.
 */

import type { JSX } from 'react';
import { memo, useId } from 'react';

import { LABEL_LINES, labelLines } from '../lib/label';
import { WITH_LINES } from '../lib/labels';
import type { LabelKind } from '../lib/labels';
import type { RecordSize } from '../lib/records';

import { LABEL_ART } from './labelArt';

export interface RecordLabelProps {
  readonly kind: LabelKind;
  readonly title: string;
  readonly performer: string;
  readonly size: RecordSize;
  readonly className?: string | undefined;
}

export const RecordLabel = memo(function RecordLabel({
  kind,
  title,
  performer,
  size,
  className,
}: RecordLabelProps): JSX.Element {
  const id = useId();
  const lines = WITH_LINES.has(kind)
    ? labelLines(title, performer, size)
    : { title: undefined, performer: undefined };
  const places = LABEL_LINES[size];
  const art = LABEL_ART[kind][size];
  // The spacing the fit counted with, written with the line: one number, not two.
  const titleSpacing = `${String(places.title.spacing)}em`;
  const performerSpacing = `${String(places.performer.spacing)}em`;

  return (
    <svg
      className={className}
      viewBox="0 0 200 200"
      aria-hidden="true"
      data-cover="label"
      data-label={kind}
      data-size={size}
    >
      <image href={art} width="200" height="200" />
      {size === '33' ? (
        <>
          {lines.title !== undefined && (
            <text
              className="labelLine labelTitle"
              letterSpacing={titleSpacing}
              x="100"
              y="140"
              fontSize={lines.title.size}
            >
              {lines.title.text}
            </text>
          )}
          {lines.performer !== undefined && (
            <text
              className="labelLine labelPerformer"
              letterSpacing={performerSpacing}
              x="100"
              y="158"
              fontSize={lines.performer.size}
            >
              {lines.performer.text}
            </text>
          )}
        </>
      ) : (
        <>
          <defs>
            <path id={`${id}-outer`} d="M 20,100 A 80,80 0 0 0 180,100" />
            <path id={`${id}-inner`} d="M 36,100 A 64,64 0 0 0 164,100" />
          </defs>
          {lines.title !== undefined && (
            <text
              className="labelLine labelTitle"
              letterSpacing={titleSpacing}
              fontSize={lines.title.size}
            >
              <textPath href={`#${id}-outer`} startOffset="50%">
                {lines.title.text}
              </textPath>
            </text>
          )}
          {lines.performer !== undefined && (
            <text
              className="labelLine labelPerformer"
              letterSpacing={performerSpacing}
              fontSize={lines.performer.size}
            >
              <textPath href={`#${id}-inner`} startOffset="50%">
                {lines.performer.text}
              </textPath>
            </text>
          )}
        </>
      )}
    </svg>
  );
});
